import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Plus, Trash2 } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  useIndicacoesDoFreelancer,
  useRemoveIndicacao,
} from "@/hooks/use-indicacoes-agencia";
import type { Agencia } from "@/types/agencia";

import { Button } from "@/components/ui/button";
import { IndicarAgenciaDialog } from "@/components/freelancers/IndicarAgenciaDialog";
import { DetailSection } from "@/components/freelancers/DetailSection";

interface FreelancerIndicacoesAgenciaProps {
  freelancerId: string;
  freelancerName: string;
  /** Quando false, renderiza sem a moldura de DetailSection (p/ usar como coluna). */
  asSection?: boolean;
}

/**
 * Seção do detalhe do freelancer: lista as agências para as quais ele está
 * indicado / não indicado, com adicionar e remover. Camada de curadoria humana
 * que complementa o engine de recomendação.
 */
export function FreelancerIndicacoesAgencia({
  freelancerId,
  freelancerName,
  asSection = true,
}: FreelancerIndicacoesAgenciaProps) {
  const { toast } = useToast();
  const { indicacoes, isLoading } = useIndicacoesDoFreelancer(freelancerId);
  const remove = useRemoveIndicacao();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Nomes de agência para exibição (reusa o cache da query ["agencias"]).
  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias")
        .select()
        .order("nome");
      if (error) throw error;
      return (data || []) as Agencia[];
    },
  });

  const nomePorAgencia = useMemo(() => {
    const map = new Map<string, string>();
    agencias.forEach((a) => map.set(a.id, a.nome));
    return map;
  }, [agencias]);

  const handleRemove = async (id: string) => {
    try {
      await remove.mutateAsync(id);
      toast({ title: "Indicação removida" });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao remover",
        description: (err as Error).message,
      });
    }
  };

  const adicionarButton = (
    <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
      <Plus className="mr-1 h-4 w-4" />
      Adicionar
    </Button>
  );

  const body = (
    <>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-9 w-full rounded-md" />
          <Skeleton className="h-9 w-2/3 rounded-md" />
        </div>
      ) : indicacoes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nenhum match com agência ainda.
        </p>
      ) : (
        <ul className="space-y-2">
          {indicacoes.map((ind) => {
            return (
              <li
                key={ind.id}
                className="flex items-start justify-between gap-2 rounded-md border p-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium">
                      {nomePorAgencia.get(ind.agencia_id) ?? "Agência"}
                    </span>
                  </div>
                  {ind.motivo && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {ind.motivo}
                    </p>
                  )}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0"
                  onClick={() => handleRemove(ind.id)}
                  disabled={remove.isPending}
                  title="Remover indicação"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <IndicarAgenciaDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        freelancerId={freelancerId}
        freelancerName={freelancerName}
      />
    </>
  );

  if (!asSection) {
    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="flex items-center gap-1.5 text-sm font-semibold">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
              Match com agências
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Agências que dão match com este perfil.
            </p>
          </div>
          {adicionarButton}
        </div>
        {body}
      </div>
    );
  }

  return (
    <DetailSection
      title="Match com agências"
      icon={Building2}
      description="Marque as agências que dão match com este perfil."
      action={adicionarButton}
    >
      {body}
    </DetailSection>
  );
}
